import { useEffect, useState } from 'react';
import { Layer, useMapInstance } from 'shared/map-exports';
import { PrivateLandOwnershipEntity } from 'shared/types';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'features/mapping/mapping.utils';
import { Map } from 'mapbox-gl';
import { debounce } from 'es-toolkit/compat';
import { VisibilityToggledSource } from 'features/mapping/components/layers/VisibilityToggledSource';
import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import {
  MapboxFeature,
  MapDrawerEntityForPoi,
} from 'features/mapping/mapping.types';
import { ON_TOP_OF_EVERYTHING_LAYER_ID } from 'features/mapping/styles';
import {
  layerBorderStyle,
  layerFillStyle,
  layerLabelStyle,
} from './layerStyles';
import {
  SOURCE_ID,
  SOURCE_DATA_POLYGONS_LAYER_ID,
  LABEL_LAYER_ID,
} from './constants';
import { EXTERNAL_LINE_LAYERS_BEFORE_ID } from '../ExternalFeatureLayer/ExternalFeatureLayer.constants';

type PrivateLandOwnershipLayerProps = {
  visible: boolean;
};

type FeatureProperties = {
  uuid: string;
};

const hideLabels = (map: Map): void => {
  if (!map.isStyleLoaded() || !map.getLayer(LABEL_LAYER_ID)) return;
  map.setLayoutProperty(LABEL_LAYER_ID, 'visibility', 'none');
};

const debouncedShowLabels = debounce(
  (map: Map) => {
    if (!map.isStyleLoaded() || !map.getLayer(LABEL_LAYER_ID)) return;
    map.setLayoutProperty(LABEL_LAYER_ID, 'visibility', 'visible');
  },
  350,
  { trailing: true },
);

export const privateLandOwnershipClickHandler = (
  geojsonFeatures: MapboxFeature<FeatureProperties>[],
): MapDrawerEntityForPoi[] => {
  const feature = geojsonFeatures[0];
  if (!feature) return [];

  const metadata: PrivateLandOwnershipEntity = {
    uuid: feature.properties.uuid,
  };

  return [
    {
      id: feature.id as number,
      type: 'privateLandOwnership',
      metadata,
    },
  ];
};

export const PrivateLandOwnershipLayer = (
  props: PrivateLandOwnershipLayerProps,
): JSX.Element => {
  const { visible } = props;
  const { mapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();
  const mapRef = useMapInstance();
  const [featuresNum, setFeaturesNum] = useState<number>(0);

  /**
   * These listeners reduce webview crashes on some mobile devices related to
   * "Too many glyphs being rendered in a tile" by hiding labels while a user
   * is zooming or panning, and showing them again once movement settles.
   */
  useMapEvent(
    ['zoomstart', 'movestart'],
    (event) => hideLabels(event.target),
    mapRef,
    visible,
  );
  useMapEvent(
    ['zoomend', 'moveend', 'style.load'],
    (event) => debouncedShowLabels(event.target),
    mapRef,
    visible,
  );

  useEffect(() => {
    const map = mapRef?.getMap();

    if (!map || !map.isStyleLoaded() || !map.getLayer(LABEL_LAYER_ID)) return;

    if (!featuresNum) {
      map.setLayoutProperty(LABEL_LAYER_ID, 'visibility', 'none');
      return;
    }

    // Empirical number chosen based on testing and experimentation.
    const DENSITY_THRESHOLD = 15000;

    if (featuresNum > DENSITY_THRESHOLD) {
      /**
       * This filter hides labels with owner names longer than 18 characters at zoom levels
       * of 16-18, and at zoom levels of 13-15 hides names longer than 12 characters. This should
       * help prevent the "Too many glyphs being rendered in a tile" warning that ends up crashing
       * the webview on some mobile devices.
       */
      map.setFilter(LABEL_LAYER_ID, [
        'all',
        ['has', 'owner'],
        [
          'case',
          ['>', ['zoom'], 15],
          ['<', ['length', ['to-string', ['get', 'owner']]], 18], // Zoom 16-18
          ['<', ['length', ['to-string', ['get', 'owner']]], 12], // Zoom 13-15
        ],
      ]);
    } else {
      map.setFilter(LABEL_LAYER_ID, ['has', 'owner']);
    }

    debouncedShowLabels(map);
  }, [featuresNum, mapRef]);

  useMapEvent(
    'sourcedata',
    (event) => {
      if (event.sourceId !== SOURCE_ID || !event.isSourceLoaded) {
        return;
      }
      const sourceFeatures = event.target.querySourceFeatures(SOURCE_ID, {
        sourceLayer: SOURCE_DATA_POLYGONS_LAYER_ID,
      });

      setFeaturesNum(sourceFeatures.length);
    },
    mapRef ?? null,
    visible,
  );

  const selectedId =
    (mapDrawerEntityForPoi?.type === 'privateLandOwnership' &&
      mapDrawerEntityForPoi?.id) ||
    '';

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    otherLayerProps: { 'source-layer': SOURCE_DATA_POLYGONS_LAYER_ID },
    selectedId,
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    otherLayerProps: { 'source-layer': SOURCE_DATA_POLYGONS_LAYER_ID },
    selectedId,
  });

  return (
    <VisibilityToggledSource
      url={getWatchdutyTileUrl('private_land_records')}
      visible={visible}
      id={SOURCE_ID}
    >
      <Layer beforeId={EXTERNAL_LINE_LAYERS_BEFORE_ID} {...layerFillStyle} />
      <Layer beforeId={EXTERNAL_LINE_LAYERS_BEFORE_ID} {...layerBorderStyle} />
      <Layer beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID} {...layerLabelStyle} />
      <Layer beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID} {...selectedFillStyle} />
      <Layer
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...selectedStrokeStyle}
      />
    </VisibilityToggledSource>
  );
};
